import { z } from "zod";
import type { Apps } from "../apps";
import {
	defaultAiAssistantInfo,
	defaultAlertConfigs,
	defaultCitationsConfig,
	defaultDeploymentsConfig,
	defaultExamConfig,
	defaultFont,
	defaultI18nConfig,
	defaultLeaderboardsConfig,
	defaultMaintenanceConfig,
	defaultPracticeTestConfig,
	defaultSessionViewConfig,
} from "./defaults";

/** BCP 47 tag: language, optional script, optional region, optional variants (e.g. en, en-US, fil-PH, zh-Hant-TW) */
const localeSchema = z
	.string()
	.regex(
		/^[a-z]{2,3}(-[a-z]{4})?(-(?:[a-z]{2}|\d{3}))?(-(?:[a-z0-9]{5,8}|\d[a-z0-9]{3}))*$/i,
		"Invalid locale tag",
	);

const internalizationSchema = z.record(localeSchema, z.string());

export const ContentStatus = z.enum([
	"IN_PROGRESS",
	"IN_REVIEW",
	"CHANGES_REQUESTED",
	"ACCEPTED",
	"REJECTED",
	"PUBLISHED",
	"ARCHIVED",
]);

export type ContentStatus = z.infer<typeof ContentStatus>;

const contentStatusFilterSchema = z.object({
	visibleStatuses: z
		.array(ContentStatus)
		.min(1, "At least one content status must be selected")
		.default(["PUBLISHED"]),
});

const landingSchema = z.object({
	title: z
		.string()
		.or(
			z
				.object({
					prefix: z.string(),
					decoratedText: z.string(),
					suffix: z.string(),
				})
				.partial(),
		)
		.optional(),
	marketText: z
		.object({
			prefix: z.string().optional(),
			suffix: z.string().optional(),
		})
		.optional(),
	subtitle: z.string().optional(),
	valueProps: z
		.array(
			z.object({
				icon: z.string(),
				title: z.string(),
				subtitle: z.string(),
			}),
		)
		.optional(),
	landingGraphics: z.string().optional(),
	consent: z
		.object({
			text: internalizationSchema.optional(),
		})
		.optional(),
});

const scoreRangeSchema = z.object({
	rangeArray: z.array(z.number()),
	range: z.string(),
	color: z.string(),
	icon: z.string(),
});

const fontSizesSchema = z.object({
	stem: z.number(),
	answer: z.number(),
	explanation: z.number().optional().default(14),
});

const purchaseSchema = z.object({
	isSubscriptionEnabled: z.boolean().optional(),
	isPaywallEnabled: z.boolean().optional().default(true),
	subheading: z.string().nullable().optional(),
	baseSku: z.string().optional(),
	paywall: z
		.object({
			title: z.string().optional(),
			subtitle: z.string().optional(),
			cta: z.string().optional(),
			disclaimer: z.string().optional(),
		})
		.optional(),
});

export const logoVariantSchema = z.object({
	light: z.object({
		uri: z.string(),
	}),
	dark: z.object({
		uri: z.string(),
	}),
});

export const logoSchema = z.object({
	icon: z.string().optional(),
	adaptiveIcon: z.string().optional(),
	splashBackgroundColor: z.string().optional(),
	splashImageWidth: z.number().optional(),
	adaptiveIconBackgroundColor: z.string().optional(),

	stacked: logoVariantSchema,
	default: logoVariantSchema,
	company: logoVariantSchema.optional(),
});

export type LogoConfigSchemaType = z.infer<typeof logoSchema>;

export const linksSchema = z.object({
	baseUrl: z.string().optional(),
	privacy: z.string(),
	tos: z.string(),
	ios: z.string().optional(),
	aiToolsTos: z.string().optional(),
	android: z.string().optional(),
	redeemSupportUrl: z.string().optional(),
	companyUrl: z.string().optional(),
	contactSupportUrl: z.string().optional(),
});

export type LinksConfigSchemaType = z.infer<typeof linksSchema>;

const revenueCatSchema = z.object({
	ios: z.string(),
	android: z.string(),
});

const appAnalyticsSchema = z.object({
	mixpanel: z.object({
		id: z.string(),
	}),
	analyticsDestinations: z
		.array(
			z.object({
				type: z.enum(["mixpanel", "segment"]),
				apiKey: z.string(),
				name: z.string().optional(),
				projectId: z.string().optional(),
			}),
		)
		.optional(),
});

const unauthorizedAuthSchema = z.object({
	title: z.string().optional(),
	subtitle: z.string().optional(),
	ctaLabel: z.string().optional(),
	ctaHref: z.string().optional(),
});
const clerkSchema = z.object({
	publishableKeyDev: z.string(),
	publishableKeyProd: z.string(),
});

const authSchema = z.object({
	tokenBasedFlow: z.boolean().optional().default(false),
	unauthorized: unauthorizedAuthSchema.optional(),
});

const appKeysSchema = z.object({
	revenueCat: revenueCatSchema,
	oneSignalAppId: z.string(),
	clerk: clerkSchema.optional().nullable(),
	airtableBase: z.string().optional(),
});

const integrationsSchema = z.object({
	slack: z
		.object({
			channel: z.string().optional(),
		})
		.optional(),
});

const sessionConfigFiltersSchema = z.array(
	z.object({
		label: internalizationSchema,
		distKey: z.string(),
		color: z.string().optional(),
	}),
);

export type SessionConfigFiltersType = z.infer<
	typeof sessionConfigFiltersSchema
>;

const sessionConfigSchema = z.object({
	showDistributionFilters: z.boolean().optional(),
	maxItemsPerSession: z.number().optional(),
	showItemsSlider: z.boolean().optional(),
	// Duration wins: when true, the item slider is not rendered even if showItemsSlider is set.
	showDurationSelection: z.boolean().optional().default(false),
	showLearningMode: z.boolean().optional(),
	filters: sessionConfigFiltersSchema.optional(),
	crossTagSelection: z.boolean().optional(),
	maxFilterDepth: z.number().int().min(1).optional().default(1),
	contentStatusFilters: contentStatusFilterSchema.optional(),
	hideRecommendedTab: z.boolean().optional().default(false),
});

const skillVariablesSchema = z.record(
	z.string(),
	z.record(z.string(), z.string()),
);

const configuredDialogSchema = z.object({
	title: z.string(),
	body: z.string(),
	skillVariables: skillVariablesSchema.optional(),
});

export type ConfiguredDialogSchemaType = z.infer<typeof configuredDialogSchema>;

const welcomeDialogSchema = z.object({
	diagnostic: configuredDialogSchema,
	disclaimer: z.string().optional(),
});

export type WelcomeDialogSchemaType = z.infer<typeof welcomeDialogSchema>;
const testDriverSchema = z.enum(["default", "pte", "toeic"]);

export type TestDriverType = z.infer<typeof testDriverSchema>;

const aiSchema = z
	.object({
		hint: z.boolean().optional(),
		aila: z.boolean().optional(),
	})
	.optional()
	.default({ hint: true, aila: true });
export type AiSchemaType = z.infer<typeof aiSchema>;
export const sessionViewConfigSchema = z.object({
	cta: z.string().optional(),
	disclaimer: z.string().optional(),
	enableSessionReplay: z.boolean().optional(),
	welcomeDialog: welcomeDialogSchema.optional(),
	helpDialog: configuredDialogSchema.optional(),
	testDriver: testDriverSchema.optional().default("default"),
	showFlightCalculator: z
		.boolean()
		.optional()
		.default(defaultSessionViewConfig.showFlightCalculator),
	maxItems: z
		.object({
			flashcards: z.number(),
			questions: z.number(),
		})
		.optional(),
	postAnswerStats: z
		.object({
			showXpEarned: z.boolean().optional(),
			showNumAttempts: z.boolean().optional(),
		})
		.optional(),
	timer: z.boolean().optional(),
	allowMarking: z
		.boolean()
		.optional()
		.default(defaultSessionViewConfig.allowMarking),
	showCrossOut: z
		.boolean()
		.optional()
		.default(defaultSessionViewConfig.showCrossOut),
	selectionStrategy: z
		.object({
			default: z.string(),
			optimized: z.boolean(),
			random: z.boolean(),
			shuffled: z.boolean(),
		})
		.optional(),
	suggestCorrection: z
		.object({
			description: z.string(),
		})
		.optional(),
	ai: aiSchema,
	configOverrides: z
		.object({
			proficiencyLabel: z.string().optional(),
		})
		.optional(),
});

const statsSchema = z.array(
	z.object({
		type: z.string(),
		title: z.string(),
		icon: z.string(),
		tooltip: z.string().optional(),
	}),
);

export type AppConfigStatsType = z.infer<typeof statsSchema>;

export const cardSchema = z.object({
	type: z.string(),
	title: internalizationSchema.optional(),
});

export type CardSchemaType = z.infer<typeof cardSchema>;

const cardsSchema = z.array(cardSchema);

export type AppConfigCardsType = z.infer<typeof cardsSchema>;

export const faqItemSchema = z.object({
	question: z.string().trim(),
	answer: z.string().trim(),
	locale: localeSchema,
	order: z.number().min(0),
});
export type FaqItemSchemaType = z.infer<typeof faqItemSchema>;
export const accountSettingsViewSchema = z.object({
	showFaqs: z.boolean().optional(),
	showPurchasedProducts: z.boolean().default(true),
	showCurrentExam: z.boolean().optional(),
	faqs: z.array(faqItemSchema).optional(),
	showLanguageSettings: z.boolean().optional(),
	showAudioTestDialog: z.boolean().optional(),
	showPartnersSection: z.boolean().optional(),
});

export const progressViewSchema = z.object({
	noScoresGraphicUrl: z.string().optional(),
});

export type ProgressViewConfig = z.infer<typeof progressViewSchema>;

const scoreDisplayModeSchema = z
	.enum(["raw", "scaled"])
	.optional()
	.default("raw");

export type ScoreDisplayMode = z.infer<typeof scoreDisplayModeSchema>;

export const reportViewConfigSchema = z.object({
	practiceTest: z.object({
		reviewEnabled: z.boolean(),
	}),
	scoreDisplayMode: scoreDisplayModeSchema,
	stats: statsSchema.optional(),
	cards: cardsSchema.optional(),
	options: z
		.object({
			showProficiencyEstimates: z.boolean().optional().default(false),
			fillPreviousProficiencyLevels: z.boolean().optional().default(true),
		})
		.optional()
		.default({
			showProficiencyEstimates: false,
			fillPreviousProficiencyLevels: true,
		}),
	ai: z
		.object({
			sessionAnalysis: z.boolean().optional().default(true),
			sessionInsights: z.boolean().optional().default(false),
		})
		.optional()
		.default({ sessionAnalysis: true, sessionInsights: false }),
	formative: z
		.object({
			showPointsBasedScore: z.boolean().optional(),
			maxScore: z.number().optional(),
			reviewEnabled: z.boolean().optional(),
		})
		.optional(),
});

export type ReportViewConfig = z.infer<typeof reportViewConfigSchema>;

export const i18nConfigSchema = z.object({
	supportedLocales: z
		.array(localeSchema)
		.default(defaultI18nConfig.supportedLocales),
	fallbackLocale: localeSchema.default(defaultI18nConfig.fallbackLocale),
	defaultToDeviceLocale: z
		.boolean()
		.default(defaultI18nConfig.defaultToDeviceLocale),
	overrides: z
		.partialRecord(localeSchema, z.record(z.string(), z.unknown()))
		.default(defaultI18nConfig.overrides),
});

export type I18nConfig = z.infer<typeof i18nConfigSchema>;

const navigationRouteSchema = z.object({
	title: z.string(),
	name: z.string(),
	icon: z.object({
		default: z.string(),
		selected: z.string(),
	}),
	enabled: z.boolean(),
	custom: z.boolean().optional(),
});

const navigationSchema = z.object({
	routes: z.object({
		main: z.array(navigationRouteSchema),
	}),
	hideStudyPack: z.boolean().optional().default(false),
});

const oldPortalSchema = z.object({
	title: z.string(),
	description: z.string(),
	link: z.string(),
});

const aiAssistantSchema = z.object({
	title: z.string().optional().default(defaultAiAssistantInfo.title),
	alias: z.string().optional().default(defaultAiAssistantInfo.alias),
	fabIcon: z.string().optional(),
	fabDarkIcon: z.string().optional(),
	icon: z.string(),
	darkIcon: z.string().optional(),
	disclaimer: z
		.object({
			enabled: z.boolean(),
			title: z.string().optional().default("Disclaimer"),
			body: z
				.string()
				.optional()
				.default(
					"**{{agentName}}** can make mistakes. Use it at your own discretion and verify important information.",
				),
		})
		.optional(),
	showOriginalQuestion: z
		.boolean()
		.optional()
		.default(defaultAiAssistantInfo.showOriginalQuestion),
	initialMessage: z
		.string()
		.optional()
		.default(defaultAiAssistantInfo.initialMessage),
	initialAiMessage: internalizationSchema.optional(),
});

const masteryLevelsSchema = z.array(
	z.object({
		id: z.string(),
		darkColor: z.string(),
		lightColor: z.string(),
		sortOrder: z.number(),
		icon: z.string(),
		range: z.object({
			type: z.string(),
			score: z.array(z.number()),
			points: z.array(z.number()),
		}),
		alias: z.object({
			title: z.string(),
		}),
		level: z.string(),
	}),
);

export type AppConfigMasteryLevelsType = z.infer<typeof masteryLevelsSchema>;

const fontSchema = z.enum([
	"opensans",
	"beausite",
	"greetnarrow",
	"greetstandard",
	"sourcesanspro",
	"calibri",
	"cambridgesans",
	"notoserif",
	"plusjakartasans",
]);

export type FontSchemaType = z.infer<typeof fontSchema>;

const citationsSchema = z.object({
	visible: z.boolean().optional().default(defaultCitationsConfig.visible),
	showTextBasedCitationOnly: z
		.boolean()
		.optional()
		.default(defaultCitationsConfig.showTextBasedCitationOnly),
});

const deploymentInfoSchema = z.object({
	id: z.string(),
	deployedAt: z.string().optional(),
	version: z.string().optional(),
});

const deploymentsSchema = z
	.object({
		autoDeploymentEnabled: z
			.boolean()
			.optional()
			.default(defaultDeploymentsConfig.autoDeploymentEnabled),
		web: z
			.object({
				pendingDeployment: deploymentInfoSchema.optional().nullable(),
				lastDeployment: deploymentInfoSchema.optional().nullable(),
			})
			.optional()
			.nullable(),
	})
	.optional();

export const leaderboardsConfigSchema = z.object({
	timePeriods: z
		.array(z.enum(["all_time", "month", "week"]))
		.optional()
		.default([...defaultLeaderboardsConfig.timePeriods]),
	scopes: z
		.array(z.enum(["global", "friends"]))
		.optional()
		.default([...defaultLeaderboardsConfig.scopes]),
	showUsersPodium: z
		.boolean()
		.optional()
		.default(defaultLeaderboardsConfig.showUsersPodium),
	numUsersToDisplay: z
		.number()
		.min(3)
		.max(25)
		.optional()
		.default(defaultLeaderboardsConfig.numUsersToDisplay),
});

const iosBuildNumberStateSchema = z.object({
	currentVersion: z.string().optional(),
	buildNumber: z.number().optional(),
});

const dnsRecordSchema = z.object({
	id: z.string(),
	type: z.enum(["A", "AAAA", "CNAME", "MX", "TXT"]),
	name: z.string(),
	value: z.string(),
	description: z.string(),
	ttl: z.string(),
	priority: z.string().optional(),
	provider: z.enum(["vercel", "resend"]).optional(),
});

export const dnsConfigSchema = z
	.object({
		domain: z.string(),
		records: z.array(dnsRecordSchema),
		betaDomain: z.string().optional(),
		betaRecords: z.array(dnsRecordSchema).optional(),
		status: z.enum(["configured", "not_configured"]).default("not_configured"),
		createdAt: z.string().optional(),
	})
	.optional();

export type DnsConfigSchemaType = z.infer<typeof dnsConfigSchema>;

export type IosBuildNumberStateSchemaType = z.infer<
	typeof iosBuildNumberStateSchema
>;

export const mobileAndroidSchema = z.object({
	versionCode: z.number().optional(),
	package: z.string().optional(),
	googleServicesJson: z.string().nullable().optional(),
	serviceAccountJson: z
		.string()
		.or(z.record(z.string(), z.unknown()))
		.nullable()
		.optional(),
});

export type MobileAndroidSchemaType = z.infer<typeof mobileAndroidSchema>;

export const mobileIosSchema = z.object({
	bundleIdentifier: z.string().optional(),
	buildNumberState: iosBuildNumberStateSchema.nullable().optional(),
});

export type MobileIosSchemaType = z.infer<typeof mobileIosSchema>;

export const mobileAppleSchema = z.object({
	teamId: z.string().optional(),
	ascIssuerId: z.string().optional(),
	ascKeyId: z.string().optional(),
	ascApiKeyP8: z.string().nullable().optional(),
	ascAppId: z.string().optional(),
});

export type MobileAppleSchemaType = z.infer<typeof mobileAppleSchema>;

export const mobileSchema = z
	.object({
		apple: mobileAppleSchema.optional(),
		android: mobileAndroidSchema.optional(),
		ios: mobileIosSchema.optional(),
	})
	.optional();

export type MobileConfigType = z.infer<typeof mobileSchema>;

const onboardingProfileFieldEnum = z.enum([
	"username",
	"first_name",
	"last_name",
	"learner_type_id",
	"target_level_id",
	"skill_ids",
	"plan_id",
	"exam_id",
	"category_id",
	"level_id",
	"state_code",
]);

export type OnboardingProfileField = z.infer<typeof onboardingProfileFieldEnum>;

const knownOnboardingProfileFields = new Set<string>(
	onboardingProfileFieldEnum.options,
);

// Unknown names and malformed values are filtered rather than rejected:
// parseAppConfig validates the whole config in one pass and falls back to raw
// unvalidated data on failure, so one typo would discard every default in it.
export const onboardingRequiredFieldsSchema = z.preprocess(
	(value) =>
		Array.isArray(value)
			? value.filter(
					(field) =>
						typeof field === "string" &&
						knownOnboardingProfileFields.has(field),
				)
			: [],
	z.array(onboardingProfileFieldEnum),
);

export const appConfigSchema = z.object({
	id: z.string(),
	name: z.string(),
	template: z
		.enum(["testprep", "cme", "language-learning"])
		.optional()
		.default("testprep"),
	proficiencyTargetScore: z.number().optional().default(90),
	maxScaledScore: z.number().optional().default(100),
	scheme: z.string().optional(),
	enableMediaPlayback: z.boolean().optional(),
	mobile: mobileSchema,
	version: z.string().optional(),
	maintenance: z
		.object({
			enabled: z.boolean(),
			title: z.string().optional().default(defaultMaintenanceConfig.title),
			urls: z
				.object({
					ios: z.string().optional(),
					android: z.string().optional(),
				})
				.optional(),
			subtitle: z
				.string()
				.optional()
				.default(defaultMaintenanceConfig.subtitle),
		})
		.optional(),

	font: fontSchema.optional().default(defaultFont),
	trademark: z.string().optional(),
	showDiagnostic: z.boolean().optional(),
	showPracticeTests: z.boolean().optional(),
	purchase: purchaseSchema.optional(),
	masteryLabels: z
		.object({
			low: z.string(),
			medium: z.string(),
			high: z.string(),
			none: z.string(),
		})
		.optional(),
	masteryLevels: masteryLevelsSchema.optional(),
	proficiencyLevels: masteryLevelsSchema.optional(),
	scoreRanges: z.array(scoreRangeSchema).optional(),
	hideTotalItemCounts: z.boolean().optional().default(false),
	fontSizes: fontSizesSchema.optional(),
	componentOverrides: z.record(z.string(), z.any()).optional(),
	analytics: appAnalyticsSchema,
	logo: logoSchema,
	links: linksSchema,
	keys: appKeysSchema,
	auth: authSchema.optional(),
	integrations: integrationsSchema.optional(),
	supportUrl: z.string().optional(),
	tagType: internalizationSchema.optional().default({
		"en-US": "Topic",
	}),
	oldPortal: oldPortalSchema.optional(),
	aiAssistant: aiAssistantSchema.optional().default(defaultAiAssistantInfo),
	isImageChallengeApp: z.boolean().optional().default(false),
	isStudyPackEnabled: z.boolean().optional().default(true),
	landing: landingSchema.optional(),
	progressView: progressViewSchema.optional(),
	showCountrySelection: z.boolean().optional().default(false),
	consent: z
		.object({
			text: internalizationSchema.optional(),
		})
		.optional(),
	avatarType: z
		.enum([
			"initials",
			"disco",
			"glass",
			"identicon",
			"shapes",
			"stripes",
			"thumbs",
			"triangles",
		])
		.optional()
		.default("initials"),
	sessionConfig: sessionConfigSchema.optional(),
	accountSettings: accountSettingsViewSchema.nullable().default({
		showPurchasedProducts: true,
	}),
	appAvailabilityRegions: z
		.array(z.string())
		.optional()
		.describe(
			"Array of ISO 3166-1 alpha-2 country codes where app is available. Empty or undefined means available everywhere",
		),
	citations: citationsSchema.optional(),
	deployments: deploymentsSchema.optional().default(defaultDeploymentsConfig),
	leaderboards: leaderboardsConfigSchema.optional().default({
		...defaultLeaderboardsConfig,
		timePeriods: [...defaultLeaderboardsConfig.timePeriods],
		scopes: [...defaultLeaderboardsConfig.scopes],
	}),
	dns: dnsConfigSchema,
	i18n: i18nConfigSchema.optional().default(defaultI18nConfig),
	onboarding: z
		.object({
			requiredFields: onboardingRequiredFieldsSchema.optional(),
		})
		.optional(),
});

export type AppConfigSchemaType = z.infer<typeof appConfigSchema>;

export type DeploymentInfoSchemaType = z.infer<typeof deploymentInfoSchema>;
export type DeploymentsSchemaType = z.infer<typeof deploymentsSchema>;

const colorValueSchema = z
	.string()
	.trim()
	.refine(
		(value) => {
			const rgbPattern = /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/;
			const rgbaPattern =
				/^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[0-9]*\.?[0-9]+\s*\)$/;
			const hexPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3}|[A-Fa-f0-9]{8})$/;
			const namedColors = ["transparent", "inherit", "currentColor"];

			return (
				rgbPattern.test(value) ||
				rgbaPattern.test(value) ||
				hexPattern.test(value) ||
				namedColors.includes(value)
			);
		},
		{
			error:
				"Invalid color format. Use rgb(), rgba(), hex (#), or named colors.",
		},
	);

const elevationSchema = z.object({
	level0: colorValueSchema,
	level1: colorValueSchema,
	level2: colorValueSchema,
	level3: colorValueSchema,
	level4: colorValueSchema,
	level5: colorValueSchema,
});

const levelColorsSchema = z.object({
	low: colorValueSchema,
	medium: colorValueSchema,
	high: colorValueSchema,
});

export const colorModeSchema = z.object({
	primary: colorValueSchema,
	onPrimary: colorValueSchema,
	primaryContainer: colorValueSchema,
	onPrimaryContainer: colorValueSchema,

	secondary: colorValueSchema,
	onSecondary: colorValueSchema,
	secondaryContainer: colorValueSchema,
	onSecondaryContainer: colorValueSchema,

	tertiary: colorValueSchema,
	onTertiary: colorValueSchema,
	tertiaryContainer: colorValueSchema,
	onTertiaryContainer: colorValueSchema,

	error: colorValueSchema,
	onError: colorValueSchema,
	errorContainer: colorValueSchema,
	onErrorContainer: colorValueSchema,

	surface: colorValueSchema,
	onSurface: colorValueSchema,
	surfaceVariant: colorValueSchema,
	onSurfaceVariant: colorValueSchema,
	surfaceDisabled: colorValueSchema,
	onSurfaceDisabled: colorValueSchema,

	background: colorValueSchema,
	onBackground: colorValueSchema,

	inversePrimary: colorValueSchema,
	inverseSurface: colorValueSchema,
	inverseOnSurface: colorValueSchema,

	outline: colorValueSchema,
	outlineVariant: colorValueSchema,

	scrim: colorValueSchema,
	shadow: colorValueSchema,
	backdrop: colorValueSchema,

	neutral: colorValueSchema.optional(),

	progress: levelColorsSchema.optional(),
	confidence: levelColorsSchema.optional(),

	elevation: elevationSchema,
});

export type ColorModeSchemaType = z.infer<typeof colorModeSchema>;

export const structuredThemeSchema = z.object({
	colors: z.object({
		light: colorModeSchema,
		dark: colorModeSchema,
	}),
	roundness: z.number().optional(),
});

export type StructuredThemeSchemaType = z.infer<typeof structuredThemeSchema>;

const themeSchema = z.record(z.string(), z.any());

export const examBrandingSchema = z.object({
	theme: themeSchema.optional(),
	logo: z
		.object({
			default: logoVariantSchema.optional(),
			stacked: logoVariantSchema.optional(),
		})
		.optional(),
});

export type ExamBrandingSchemaType = z.infer<typeof examBrandingSchema>;

const alertItemSchema = z.object({
	description: z.string(),
	icon: z.string(),
	severity: z.enum(["info", "warning", "error"]),
	title: z.string().optional(),
});

const alertConfigSchema = z.object({
	quiz: alertItemSchema.default(defaultAlertConfigs.quiz),
	practiceTest: alertItemSchema.default(defaultAlertConfigs.practiceTest),
	quizAndPracticeTest: alertItemSchema.default(
		defaultAlertConfigs.quizAndPracticeTest,
	),
	topicMastery: alertItemSchema.default(defaultAlertConfigs.topicMastery),
});

const summativeTestAlertConfigSchema = alertConfigSchema
	.optional()
	.default(defaultAlertConfigs);

export type SummativeTestAlertConfigType = z.infer<typeof alertConfigSchema>;

const practiceTestConfigSchema = z
	.object({
		header: z
			.object({
				title: z.string(),
				subtitle: z.string(),
			})
			.optional()
			.default(defaultPracticeTestConfig.header),
		label: z.string().optional().default(defaultPracticeTestConfig.label),
		summativeTestAlertConfig: summativeTestAlertConfigSchema,
		enabled: z
			.object({
				mobile: z.boolean().default(defaultPracticeTestConfig.enabled.mobile),
				web: z.boolean().default(defaultPracticeTestConfig.enabled.web),
			})
			.optional()
			.default(defaultPracticeTestConfig.enabled),
		deviceRecommendationDialog: z
			.object({
				enabled: z.boolean(),
				title: z.string(),
				message: z.string(),
			})
			.optional()
			.default(defaultPracticeTestConfig.deviceRecommendationDialog),
	})
	.optional();

const onboardingStepTypeEnum = z.enum([
	"welcome",
	"profile",
	"privacy",
	"learnerType",
	"chooseCategory",
	"chooseLevel",
	"chooseState",
	"chooseExam",
	"chooseContext",
	"examDate",
	"set-date",
	"rank",
	"skill",
	"plan",
	"toeicLevelOverview",
	"review",
]);

export type OnboardingStepType = z.infer<typeof onboardingStepTypeEnum>;

const onboardingDataItemSchema = z.object({
	titlei18n: internalizationSchema.optional(),
	descriptioni18n: internalizationSchema.optional(),
	icon: z.string().optional(),
	iconString: z.string().optional(),
	type: z.string().optional(),
	image: z.string().optional(),
	badgei18n: internalizationSchema.optional(),
});

export type OnboardingDataItemType = z.infer<typeof onboardingDataItemSchema>;

export const onboardingStepSchema = z.object({
	step: onboardingStepTypeEnum,
	enabled: z.boolean(),
	isMobile: z.boolean().optional(),
	titlei18n: internalizationSchema,
	labeli18n: internalizationSchema.optional(),
	descriptioni18n: internalizationSchema.optional(),
	data: z.array(onboardingDataItemSchema).optional(),
	graphics: z
		.object({
			desktop: z.string(),
			tablet: z.string().optional(),
			mobile: z.string().optional(),
		})
		.optional(),
});

export type OnboardingStep = z.infer<typeof onboardingStepSchema>;

const knownOnboardingStepTypes = new Set<string>(
	onboardingStepTypeEnum.options,
);

const hasKnownStepType = (value: unknown): boolean =>
	typeof value === "object" &&
	value !== null &&
	knownOnboardingStepTypes.has((value as { step?: unknown }).step as string);

export const onboardingStepsSchema = z.preprocess(
	(value) => (Array.isArray(value) ? value.filter(hasKnownStepType) : value),
	z.array(onboardingStepSchema),
);

export const tenantSchema = z.object({
	id: z.string(),
	name: z.string(),
});

export type TenantSchemaType = z.infer<typeof tenantSchema>;

export const customConfigTypeSchema = z.object({
	id: z.string(),
	tenant: tenantSchema,
	supportEmail: z.string(),
	companyName: z.string(),
	app: appConfigSchema,
	groupByCategory: z.boolean().optional().nullable(),
	exam: z.object({
		enabled: z.boolean(),
		whiteListedExamIds: z.array(z.string()).optional(),
		quizEnabled: z.boolean().optional(),
		allowChangeExam: z.boolean().optional(),
		allowChangeExamDate: z
			.boolean()
			.optional()
			.default(defaultExamConfig.allowChangeExamDate),
		label: z.string().optional(),
		diagnosticLabel: z.record(z.string(), z.string()).optional(),
		taskCardSubtitle: z.string().optional(),
		practiceTest: practiceTestConfigSchema
			.optional()
			.default(defaultExamConfig.practiceTest),
		examDateDisclaimer: z.string().optional(),
		brandingProfiles: z.record(z.string(), examBrandingSchema).optional(),
		branding: z.record(z.string(), z.string()).optional(),
	}),
	store: z.object({
		enabled: z.boolean(),
	}),
	homeView: z
		.object({
			showRecommendedChallenge: z.boolean().optional().default(false),
			showArticles: z.boolean().optional().default(false),
			showChallengeOfTheWeek: z.boolean().optional().default(false),
			masterySubheaderLabel: z.string().optional(),
			showRedeem: z.boolean().optional().default(false),
			headerIconMode: z
				.enum(["none", "exam", "company"])
				.optional()
				.default("exam"),
		})
		.optional()
		.nullable(),
	showStudyPackAsHome: z.boolean().optional().nullable(),
	reportView: reportViewConfigSchema.optional().nullable(),
	onboarding: onboardingStepsSchema.optional().nullable(),
	navigation: navigationSchema.optional().nullable(),
	sessionView: sessionViewConfigSchema.optional().nullable(),
	theme: themeSchema,
	createdAt: z.string().optional(),
	updatedAt: z.string().optional(),
});

export type CustomConfigType = z.infer<typeof customConfigTypeSchema>;

export type AvatarType = NonNullable<CustomConfigType["app"]["avatarType"]>;

export type ConfigMap = Record<Apps, CustomConfigType>;
